import mermaid from "mermaid";
import { marked } from "marked";

const RED = "D52B3B";
mermaid.initialize({ startOnLoad: false, securityLevel: "strict", theme: "base", htmlLabels: false, flowchart: { htmlLabels: false, curve: "basis" },
  themeVariables: { primaryColor: "#FDECEE", primaryBorderColor: "#D52B3B", primaryTextColor: "#1C1C1E", lineColor: "#64748B", fontFamily: "Arial, sans-serif", fontSize: "14px", clusterBkg: "#F8FAFC", clusterBorder: "#CBD5E1" } });

export const loadGuide = async () => (await fetch("/process-guide.md")).text();
export const loadDoc = async (url) => (await fetch(url)).text();
const GUIDE = { title: "EUDR Process Guide", file: "EP-Group-EUDR-Process-Guide" };
export const guideTokens = (md) => marked.lexer(md);

let seq = 0;
export async function renderMermaid(code) {
  const { svg } = await mermaid.render(`mmd-${Date.now()}-${seq++}`, code);
  return svg;
}

export function svgToPng(svg, scale = 2) {
  return new Promise((resolve, reject) => {
    const doc = new DOMParser().parseFromString(svg.replace(/<br\s*>/gi, "<br/>"), "image/svg+xml").documentElement;
    if (doc.nodeName === "parsererror" || doc.querySelector("parsererror")) { reject(new Error("diagram SVG could not be parsed")); return; }
    const vb = (doc.getAttribute("viewBox") || "0 0 800 600").split(/[\s,]+/).map(Number);
    const w = vb[2], h = vb[3];
    doc.setAttribute("width", w); doc.setAttribute("height", h);
    const img = new Image();
    img.onload = () => {
      const pad = 16;
      const c = document.createElement("canvas");
      c.width = Math.ceil((w + 2 * pad) * scale); c.height = Math.ceil((h + 2 * pad) * scale);
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, pad * scale, pad * scale, w * scale, h * scale);
      resolve({ dataUrl: c.toDataURL("image/png"), width: w + 2 * pad, height: h + 2 * pad });
    };
    img.onerror = () => reject(new Error("diagram image could not be rendered"));
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(new XMLSerializer().serializeToString(doc));
  });
}

export async function diagramsAsPng(tokens) {
  const out = [];
  for (const t of tokens) if (t.type === "code" && t.lang === "mermaid") out.push(await svgToPng(await renderMermaid(t.text)));
  return out;
}

const dataUrlBytes = (u) => Uint8Array.from(atob(u.split(",")[1]), (c) => c.charCodeAt(0));
const fetchBytes = async (url) => new Uint8Array(await (await fetch(url)).arrayBuffer());
const plain = (s) => (s || "").replace(/\*\*/g, "").replace(/\*/g, "").replace(/`/g, "").replace(/&amp;/g, "&").replace(/&gt;/g, ">").replace(/&lt;/g, "<").replace(/&#39;/g, "'").replace(/&quot;/g, '"');
// jsPDF's built-in Helvetica is WinAnsi-only: "→" breaks width measurement and letter-spaces the whole cell
const pt = (s) => plain(s).replace(/→/g, "->");
// split inline markdown into [{text, bold}] (bold + code only; everything else is plain text)
export const runs = (s) => (s || "").split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((p) => (p.startsWith("**") ? { text: plain(p.slice(2, -2)), bold: true } : { text: plain(p), bold: false }));

function save(blob, name) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

export async function downloadZip(tokens) {
  const { default: JSZip } = await import("jszip");
  const pngs = await diagramsAsPng(tokens);
  const titles = [];
  let h = "chart";
  for (const t of tokens) {
    if (t.type === "heading") h = plain(t.text);
    if (t.type === "code" && t.lang === "mermaid") titles.push(h);
  }
  const zip = new JSZip();
  pngs.forEach((p, i) => zip.file(`${String(i + 1).padStart(2, "0")}-${titles[i].toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60)}.png`, dataUrlBytes(p.dataUrl)));
  save(await zip.generateAsync({ type: "blob" }), "EP-Group-EUDR-flow-charts.zip");
}

export async function downloadDocx(tokens, opts = GUIDE) {
  const d = await import("docx");
  const { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, ImageRun, Footer, Header, AlignmentType, PageNumber, ShadingType, BorderStyle, PageOrientation } = d;
  const pngs = await diagramsAsPng(tokens);
  const logo = await fetchBytes("/logo-epg.png");
  const tr = (s, extra = {}) => runs(s).map((r) => new TextRun({ text: r.text, bold: r.bold || extra.bold, italics: extra.italics, color: extra.color, size: extra.size, font: "Arial" }));
  const border = { style: BorderStyle.SINGLE, size: 4, color: "CBD5E1" };
  const cell = (s, head) => new TableCell({ children: [new Paragraph({ children: tr(s, head ? { bold: true, color: "FFFFFF", size: 18 } : { size: 18 }) })],
    shading: head ? { type: ShadingType.CLEAR, color: "auto", fill: RED } : undefined, margins: { top: 60, bottom: 60, left: 80, right: 80 },
    borders: { top: border, bottom: border, left: border, right: border } });
  const body = [], sections = [];
  let img = 0;
  const HL = { 1: HeadingLevel.TITLE, 2: HeadingLevel.HEADING_1, 3: HeadingLevel.HEADING_2, 4: HeadingLevel.HEADING_3 };
  for (const t of tokens) {
    if (t.type === "heading") body.push(new Paragraph({ heading: HL[t.depth] || HeadingLevel.HEADING_3, spacing: { before: 240, after: 120 }, children: tr(t.text, { color: t.depth <= 2 ? RED : "1C1C1E", bold: true, size: t.depth === 1 ? 40 : t.depth === 2 ? 30 : 24 }) }));
    else if (t.type === "paragraph") body.push(new Paragraph({ spacing: { after: 120 }, children: tr(t.text, { size: 21 }) }));
    else if (t.type === "blockquote") body.push(new Paragraph({ spacing: { after: 120 }, keepLines: true, border: { left: { style: BorderStyle.SINGLE, size: 18, color: RED, space: 8 } }, children: tr(t.text, { italics: true, size: 20, color: "475569" }) }));
    else if (t.type === "list") t.items.forEach((it) => body.push(new Paragraph({ bullet: { level: 0 }, spacing: { after: 60 }, children: tr(it.text, { size: 21 }) })));
    else if (t.type === "table") body.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [new TableRow({ tableHeader: true, children: t.header.map((h) => cell(h.text, true)) }), ...t.rows.map((r) => new TableRow({ children: r.map((c) => cell(c.text)) }))] }), new Paragraph({ text: "" }));
    else if (t.type === "code" && t.lang === "mermaid") {
      const p = pngs[img++];
      const wide = p.width / p.height > 1.45;
      const maxW = wide ? 900 : 620, maxH = wide ? 520 : 820;
      let w = maxW, h = Math.round((p.height * w) / p.width);
      if (h > maxH) { w = Math.round((w * maxH) / h); h = maxH; }
      const para = new Paragraph({ alignment: AlignmentType.CENTER, spacing: { before: 120, after: 200 }, children: [new ImageRun({ type: "png", data: dataUrlBytes(p.dataUrl), transformation: { width: w, height: h } })] });
      if (wide) { sections.push({ landscape: false, children: body.splice(0) }, { landscape: true, children: [para] }); } else body.push(para);
    }
  }
  sections.push({ landscape: false, children: body });
  const header = () => new Header({ children: [new Paragraph({ border: { bottom: { style: BorderStyle.SINGLE, size: 12, color: RED, space: 4 } }, children: [new ImageRun({ type: "png", data: logo, transformation: { width: 96, height: 34 } }), new TextRun({ text: `\t${opts.title}`, color: "64748B", size: 18 })], tabStops: [{ type: "right", position: 9900 }] })] });
  const footer = () => new Footer({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ children: ["EP Group · Page ", PageNumber.CURRENT, " of ", PageNumber.TOTAL_PAGES], size: 16, color: "64748B" })] })] });
  const doc = new Document({
    creator: "EP Group", title: `EP Group — ${opts.title}`,
    styles: { default: { document: { run: { font: "Arial" } } } },
    sections: sections.filter((s) => s.children.length).map((s) => ({
      properties: { page: { size: { orientation: s.landscape ? PageOrientation.LANDSCAPE : PageOrientation.PORTRAIT }, margin: { top: 1300, bottom: 1100, left: 1000, right: 1000 } } },
      headers: { default: header() }, footers: { default: footer() }, children: s.children,
    })),
  });
  save(await Packer.toBlob(doc), `${opts.file}.docx`);
}

export async function downloadPdf(tokens, opts = GUIDE) {
  const { jsPDF } = await import("jspdf");
  const { default: autoTable } = await import("jspdf-autotable");
  const pngs = await diagramsAsPng(tokens);
  const logo = await svgLessPng("/logo-epg.png");
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, M = 16, TOP = 26, BOT = 280, CW = W - 2 * M;
  let y = TOP;
  let onBreak = null;
  const ensure = (h) => { if (y + h > BOT) { onBreak?.(); pdf.addPage(); y = TOP; } };
  const rich = (s, { size = 10, color = [28, 28, 30], indent = 0, italic = false, bullet = false } = {}) => {
    pdf.setFontSize(size); pdf.setTextColor(...color);
    const lh = size * 0.45;
    const words = runs(s).flatMap((r) => pt(r.text).split(/(\s+)/).filter((w) => w).map((w) => ({ w, bold: r.bold })));
    let x = M + indent;
    ensure(lh);
    if (bullet) { pdf.setFont("helvetica", "bold"); pdf.text("•", M + indent - 4, y); }
    for (const { w, bold } of words) {
      pdf.setFont("helvetica", bold ? "bold" : italic ? "italic" : "normal");
      const ww = pdf.getTextWidth(w);
      if (/^\s+$/.test(w)) { if (x > M + indent) x += ww; continue; }
      if (x + ww > M + CW && x > M + indent) { y += lh; ensure(lh); x = M + indent; }
      pdf.text(w, x, y); x += ww;
    }
    y += lh + 1.6;
  };
  let img = 0;
  for (const t of tokens) {
    if (t.type === "heading") {
      const size = t.depth === 1 ? 20 : t.depth === 2 ? 15 : 12;
      ensure(size * 0.6 + 10 + 60); y += t.depth === 1 ? 2 : 4;
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(size); pdf.setTextColor(...(t.depth <= 2 ? [213, 43, 59] : [28, 28, 30]));
      pdf.splitTextToSize(pt(t.text), CW).forEach((l) => { pdf.text(l, M, y); y += size * 0.45; });
      y += 2;
    } else if (t.type === "paragraph") rich(t.text);
    else if (t.type === "blockquote") {
      // callout bar drawn per page: a page break inside the callout closes the bar on this page and restarts it on the next
      // (previously one line from the start y on page N to the end y on page N+1 ran down the whole new page)
      ensure(10);
      let y0 = y - 3.6;
      const bar = (y1) => { if (y1 > y0) { pdf.setDrawColor(213, 43, 59); pdf.setLineWidth(0.8); pdf.line(M + 1, y0, M + 1, y1); } };
      onBreak = () => { bar(Math.min(y, BOT) - 3); y0 = TOP - 3.6; };
      rich(t.text, { italic: true, color: [71, 85, 105], indent: 4 });
      onBreak = null;
      bar(y - 2.4);
    }
    else if (t.type === "list") t.items.forEach((it) => rich(it.text, { indent: 5, bullet: true }));
    else if (t.type === "hr") y += 2;
    else if (t.type === "table") {
      autoTable(pdf, { startY: y, margin: { left: M, right: M, top: TOP, bottom: 297 - BOT }, head: [t.header.map((h) => pt(h.text))], body: t.rows.map((r) => r.map((c) => pt(c.text))),
        styles: { fontSize: 8, cellPadding: 1.6, lineColor: [203, 213, 225], lineWidth: 0.2, textColor: [28, 28, 30] }, headStyles: { fillColor: [213, 43, 59], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [248, 250, 252] }, theme: "grid", rowPageBreak: "avoid" });
      y = pdf.lastAutoTable.finalY + 5;
    } else if (t.type === "code" && t.lang === "mermaid") {
      const p = pngs[img++];
      if (p.width / p.height > 1.45) {
        // wide LR chart: own landscape page so the text stays readable
        pdf.addPage("a4", "landscape");
        const LW = 297 - 2 * M, LH = 210 - TOP - 16;
        let w = LW, h = (p.height * w) / p.width;
        if (h > LH) { w = (w * LH) / h; h = LH; }
        pdf.addImage(p.dataUrl, "PNG", M + (LW - w) / 2, TOP + 2, w, h, `diagram${img}`, "FAST");
        pdf.addPage("a4", "portrait"); y = TOP;
        continue;
      }
      let w = CW, h = (p.height * w) / p.width;
      const maxH = BOT - TOP - 4;
      if (h > maxH) { w = (w * maxH) / h; h = maxH; }
      ensure(h + 4);
      pdf.addImage(p.dataUrl, "PNG", M + (CW - w) / 2, y, w, h, `diagram${img}`, "FAST"); y += h + 6;
    }
  }
  if (y === TOP && pdf.getNumberOfPages() > 1) pdf.deletePage(pdf.getNumberOfPages());
  const n = pdf.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    pdf.setPage(i);
    const PW = pdf.internal.pageSize.getWidth(), PH = pdf.internal.pageSize.getHeight();
    pdf.addImage(logo.dataUrl, "PNG", M, 8, 26, (26 * logo.height) / logo.width, "epglogo", "FAST");
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(8); pdf.setTextColor(100, 116, 139);
    pdf.text(opts.title, PW - M, 14, { align: "right" });
    pdf.setDrawColor(213, 43, 59); pdf.setLineWidth(0.6); pdf.line(M, 18, PW - M, 18);
    pdf.text(`EP Group · Page ${i} of ${n}`, PW / 2, PH - 9, { align: "center" });
  }
  pdf.save(`${opts.file}.pdf`);
}

function svgLessPng(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      c.getContext("2d").drawImage(img, 0, 0);
      resolve({ dataUrl: c.toDataURL("image/png"), width: c.width, height: c.height });
    };
    img.onerror = reject;
    img.src = url;
  });
}
